import { __awaiter } from "tslib";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { findSkuInfo } from '@as-react/common-queries';
import { Banner, LinkButton } from '@yonderland/andes-react';
import { useIsClient } from '@as/cotopaxi';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { basketActions } from '../../../../redux/basket/actions/BasketActions.creators';
import { makeGetBasketMappedProductsState, makeGetBasketPromotionProductsState, makeGetBasketTotalQuantityState, } from '../../../../redux/basket/selectors/BasketSelector';
import { getQuerystringByName, removeQuerystringParameter } from '../../../../util/URLParamUtil';
import { EventEnum } from '@as-react/common-types';
import { EventUtil } from '@as-react/common-utils';
import { ADOBE_TARGET_PRESOLDEN } from '../../../../constants/AdobeTargetConstants';
import YonderlandMediator from '../../../../mediator/YonderlandMediator';
import { YonderlandMediatorGroup } from '../../../../mediator/types/YonderlandMediatorTypes';
import { ADD_VOUCHER_PARAM_KEY } from '../../../../constants/BasketConstants';
import { useBasketOverviewConfig } from '../../../../context/ComponentConfigContext';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
const PROMOTION_MESSAGE_DISCOUNT_VARIABLE = '{DISCOUNT}';
export const PromotionMessage = () => {
    var _a, _b;
    const { t } = useTranslation(I18N_NAMESPACES.checkout);
    const { mainWebShopId, preSolden } = useBasketOverviewConfig();
    const { currency, market, fictiveWebShop, servicesEndpoint } = useAemConfig();
    const [promotionTotalDiscount, setPromotionTotalDiscount] = useState(0);
    const basketTotalAmount = useSelector(makeGetBasketTotalQuantityState());
    const promotionProducts = useSelector(makeGetBasketPromotionProductsState());
    const products = useSelector(makeGetBasketMappedProductsState());
    const dispatch = useDispatch();
    const isClient = useIsClient();
    const promotion = (_a = promotionProducts.find(product => !!product.promotion)) === null || _a === void 0 ? void 0 : _a.promotion;
    const recommendedProduct = promotion === null || promotion === void 0 ? void 0 : promotion.recommendedProduct;
    const minimumBasketSize = (_b = promotion === null || promotion === void 0 ? void 0 : promotion.minimumBasketSize) !== null && _b !== void 0 ? _b : 0;
    const addVoucherParam = isClient ? getQuerystringByName(window.location.search, ADD_VOUCHER_PARAM_KEY) : false;
    const promotionRequirementsFulfilled = basketTotalAmount >= minimumBasketSize;
    const preSoldenMessage = preSolden === null || preSolden === void 0 ? void 0 : preSolden.presoldenMessage;
    const queryClient = useQueryClient();
    const onAddPromotionCalled = () => {
        removeQuerystringParameter(ADD_VOUCHER_PARAM_KEY);
        YonderlandMediator.publish({
            event: ADOBE_TARGET_PRESOLDEN,
            group: YonderlandMediatorGroup.BASKET,
            data: {
                origin: 'BasketOverviewPromotionMessage',
                hasPreSoldenProduct: true,
                isPreSoldenFulfilled: true,
                hasMultipleProducts: true,
            },
        });
    };
    useEffect(() => {
        EventUtil.listen(EventEnum.BASKET_PROMOTION_CALLED, onAddPromotionCalled);
        return () => {
            EventUtil.remove(EventEnum.BASKET_PROMOTION_CALLED, onAddPromotionCalled);
        };
    }, []);
    useEffect(() => {
        addVoucherParam && recommendedProduct && dispatch(basketActions.createAddPromotionAction(recommendedProduct));
        if (promotionProducts.length <= 0 || promotionRequirementsFulfilled)
            return;
        const getAllDiscountPrices = (allProducts) => __awaiter(void 0, void 0, void 0, function* () {
            return yield Promise.all(allProducts.map((product) => __awaiter(void 0, void 0, void 0, function* () {
                const productPrice = yield queryClient.fetchQuery({
                    queryKey: ['findSkuInfo', product.sku, market, fictiveWebShop],
                    queryFn: () => findSkuInfo(market, mainWebShopId, Number(fictiveWebShop), parseInt(product.productId, 10), product.sku, undefined, {
                        servicesEndpoint,
                    }),
                });
                return productPrice && productPrice.productDiscountAmount
                    ? productPrice.productDiscountAmount * product.quantity
                    : 0;
            })));
        });
        getAllDiscountPrices(promotionProducts).then(response => {
            setPromotionTotalDiscount(response.reduce((acc, price) => acc + price, 0));
        });
    }, [products, basketTotalAmount, promotionProducts, addVoucherParam]);
    if (addVoucherParam && !recommendedProduct && !promotionRequirementsFulfilled) {
        return _jsx(Banner, { status: "error", text: t('checkout.basket.promotion.error.voucher') });
    }
    if (!preSoldenMessage || !recommendedProduct || promotionRequirementsFulfilled)
        return null;
    const linkRegexp = /\[(.*)\]/;
    const linkMatch = new RegExp(linkRegexp).exec(preSoldenMessage);
    const link = linkMatch ? linkMatch[1] : '';
    const messageParts = preSoldenMessage
        .replace(PROMOTION_MESSAGE_DISCOUNT_VARIABLE, promotionTotalDiscount > 0 ? `${currency}${promotionTotalDiscount.toFixed(2)}` : '')
        .split(linkRegexp);
    const addPromotionToBasket = (e) => {
        e.preventDefault();
        dispatch(basketActions.createAddPromotionAction(recommendedProduct));
    };
    return (_jsx(Banner, { status: "informative", text: _jsx(_Fragment, { children: messageParts.map((part, index) => (_jsx(Fragment, { children: (part === link && _jsx(LinkButton, { onClick: addPromotionToBasket, text: link })) || part }, `${part}_${index}`))) }) }));
};
